/**
 * DirectLogin — entrada a Galia sin pasar por Apolearn (fase 1).
 *
 * Acuerdo con Santiago (24/08/2026): el estudiante entra a la app, escribe su
 * correo, recibe un código de 6 dígitos y queda identificado. Quien autoriza es
 * el roster de AEC, no una contraseña: no queremos ser custodios de contraseñas
 * de estudiantes, varios menores de edad.
 * Plan: docs/security/acceso-directo-fase1-plan.md
 *
 * Este componente es solo la cara del flujo. Toda la decisión ocurre en el
 * servidor (api/auth/request-code.ts y api/auth/verify-code.ts): aquí no se
 * valida nada que importe, ni se sabe si un correo existe — el paso 1 responde
 * lo mismo en todos los casos, a propósito.
 */

import { useEffect, useRef, useState } from 'react';
import './DirectLogin.css';
import sessionService, { SessionIdentity } from '../../services/sessionService';

/** Segundos antes de poder pedir otro código. Suaviza el rate limit del servidor. */
const REENVIO_S = 45;

interface DirectLoginProps {
  /** Se llama con la identidad ya guardada en sessionService. */
  onAuthenticated: (identity: SessionIdentity) => void;
}

export default function DirectLogin({ onAuthenticated }: DirectLoginProps) {
  const [paso, setPaso] = useState<'correo' | 'codigo'>('correo');
  const [correo, setCorreo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [espera, setEspera] = useState(0);

  const codigoRef = useRef<HTMLInputElement>(null);

  // Cuenta regresiva del reenvío.
  useEffect(() => {
    if (espera <= 0) return;
    const t = setTimeout(() => setEspera((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [espera]);

  // El foco al campo del código ahorra un toque en móvil, que es donde el
  // estudiante va a estar leyendo el correo.
  useEffect(() => {
    if (paso === 'codigo') codigoRef.current?.focus();
  }, [paso]);

  async function pedirCodigo(e?: React.FormEvent) {
    e?.preventDefault();
    if (enviando) return;
    setError(null);
    setEnviando(true);
    try {
      const res = await fetch('/api/auth/request-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: correo }),
      });
      if (!res.ok) {
        // Solo llega aquí si el correo está mal formado (400): el resto de
        // casos responden 200 sin decir si el correo existe.
        const data = await res.json().catch(() => ({}));
        setError(data?.error || 'Revisa el correo e inténtalo de nuevo.');
        return;
      }
      setPaso('codigo');
      setEspera(REENVIO_S);
    } catch {
      setError('No pudimos conectarnos. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setEnviando(false);
    }
  }

  async function verificar(e?: React.FormEvent) {
    e?.preventDefault();
    if (enviando || codigo.length !== 6) return;
    setError(null);
    setEnviando(true);
    try {
      const res = await fetch('/api/auth/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: correo, code: codigo }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(data?.error || 'No pudimos verificar el código.');
        // Código vencido o quemado: no sirve reintentar con el mismo, se vuelve
        // al paso 1 para que pida uno nuevo.
        if (data?.reason === 'expired' || data?.reason === 'too-many-attempts') {
          setPaso('correo');
          setCodigo('');
          setEspera(0);
        }
        return;
      }

      if (!data?.session || !data?.identity) {
        setError('Respuesta inesperada del servidor. Inténtalo de nuevo.');
        return;
      }

      sessionService.adopt(data.session, data.identity);
      onAuthenticated(data.identity as SessionIdentity);
    } catch {
      setError('No pudimos conectarnos. Revisa tu conexión e inténtalo de nuevo.');
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="direct-login">
      <div className="direct-login__card">
        <h1 className="direct-login__title">Bonjour&nbsp;!</h1>

        {paso === 'correo' ? (
          <form onSubmit={pedirCodigo}>
            <p className="direct-login__lead">
              Escribe el correo con el que estás inscrito en la Alianza Francesa
              y te enviamos un código para entrar.
            </p>

            <label className="direct-login__label" htmlFor="direct-login-email">
              Tu correo
            </label>
            <input
              id="direct-login-email"
              className="direct-login__input"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoFocus
              required
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              placeholder="nombre@ejemplo.com"
              disabled={enviando}
            />

            {error && <p className="direct-login__error" role="alert">{error}</p>}

            <button className="direct-login__submit" type="submit" disabled={enviando || !correo.trim()}>
              {enviando ? 'Enviando…' : 'Enviarme el código'}
            </button>
          </form>
        ) : (
          <form onSubmit={verificar}>
            <p className="direct-login__lead">
              Te enviamos un código de 6 dígitos a <strong>{correo}</strong>.
              Vence en 10 minutos.
            </p>

            <label className="direct-login__label" htmlFor="direct-login-code">
              Código
            </label>
            <input
              id="direct-login-code"
              ref={codigoRef}
              className="direct-login__input direct-login__input--code"
              type="text"
              inputMode="numeric"
              // Deja que iOS y Android ofrezcan el código del SMS/correo.
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              value={codigo}
              onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              disabled={enviando}
            />

            {error && <p className="direct-login__error" role="alert">{error}</p>}

            <button className="direct-login__submit" type="submit" disabled={enviando || codigo.length !== 6}>
              {enviando ? 'Verificando…' : 'Entrar'}
            </button>

            <div className="direct-login__foot">
              <button
                type="button"
                className="direct-login__link"
                onClick={() => pedirCodigo()}
                disabled={enviando || espera > 0}
              >
                {espera > 0 ? `Reenviar código (${espera}s)` : 'Reenviar código'}
              </button>
              <button
                type="button"
                className="direct-login__link"
                onClick={() => { setPaso('correo'); setCodigo(''); setError(null); setEspera(0); }}
                disabled={enviando}
              >
                Usar otro correo
              </button>
            </div>
          </form>
        )}

        {/* El caso que sabemos que va a pasar: el correo de AEC no siempre es el
            que el estudiante usa en Apolearn (ver plan §8). Decirlo aquí ahorra
            soporte, sin revelar qué correos existen. */}
        <p className="direct-login__hint">
          Usa el correo que le diste a la Alianza Francesa al inscribirte. Si no
          te llega nada en unos minutos, revisa la carpeta de spam.
        </p>
      </div>
    </div>
  );
}
